/** Featured gym cover framing: wide banner crop with pan + zoom. */

export type GymCoverCrop = { x: number; y: number; zoom: number };

/** Fallback for the editor when there is no hero to measure. */
export const GYM_COVER_ASPECT = 4.5;

/** Centered slightly high, matching CSS `background-position: center 47%`. */
export const DEFAULT_GYM_COVER_CROP: GymCoverCrop = { x: 0.5, y: 0.47, zoom: 1 };

export function readGymCoverCrop(header: string | null): GymCoverCrop | undefined {
  if (!header) return;
  try {
    const { x, y, zoom } = JSON.parse(header);
    if (
      [x, y, zoom].every(Number.isFinite) &&
      x >= 0 &&
      x <= 1 &&
      y >= 0 &&
      y <= 1 &&
      zoom >= 1 &&
      zoom <= 5
    )
      return { x, y, zoom };
  } catch {
    // Older or invalid framing leaves the original cover visible.
  }
}

export function gymCoverCropRect(
  width: number,
  height: number,
  crop = DEFAULT_GYM_COVER_CROP,
  aspect = GYM_COVER_ASPECT,
) {
  if (width <= 0 || height <= 0 || !Number.isFinite(width + height))
    throw new Error('That photo has invalid dimensions.');
  if (!(aspect > 0) || !Number.isFinite(aspect)) throw new Error('Cover aspect is invalid.');
  const zoom = Number.isFinite(crop.zoom) ? Math.max(1, Math.min(5, crop.zoom)) : 1;
  const fitHeight = Math.min(height, width / aspect);
  const fitWidth = fitHeight * aspect;
  const frameWidth = fitWidth / zoom;
  const frameHeight = fitHeight / zoom;
  const x = Number.isFinite(crop.x) ? crop.x : 0.5;
  const y = Number.isFinite(crop.y) ? crop.y : 0.47;
  return {
    x: Math.max(0, Math.min(width - frameWidth, x * width - frameWidth / 2)),
    y: Math.max(0, Math.min(height - frameHeight, y * height - frameHeight / 2)),
    width: frameWidth,
    height: frameHeight,
    zoom,
  };
}

export function clampGymCoverCrop(
  width: number,
  height: number,
  crop: GymCoverCrop,
  aspect = GYM_COVER_ASPECT,
): GymCoverCrop {
  const rect = gymCoverCropRect(width, height, crop, aspect);
  return {
    x: (rect.x + rect.width / 2) / width,
    y: (rect.y + rect.height / 2) / height,
    zoom: rect.zoom,
  };
}

/** Move the photo under the pointer; a pinch keeps its starting midpoint anchored. */
export function moveGymCoverCrop(
  source: { width: number; height: number },
  viewport: { width: number; height: number },
  crop: GymCoverCrop,
  from: { x: number; y: number },
  to: { x: number; y: number },
  magnification = 1,
) {
  const aspect = viewport.width / viewport.height;
  const rect = gymCoverCropRect(source.width, source.height, crop, aspect);
  const zoom = Math.max(1, Math.min(5, rect.zoom * magnification));
  const factor = rect.zoom / zoom;
  return clampGymCoverCrop(
    source.width,
    source.height,
    {
      x:
        (rect.x + ((from.x + (viewport.width / 2 - to.x) * factor) * rect.width) / viewport.width) /
        source.width,
      y:
        (rect.y +
          ((from.y + (viewport.height / 2 - to.y) * factor) * rect.height) / viewport.height) /
        source.height,
      zoom,
    },
    aspect,
  );
}
